/*
 *  -----------------------------------------------------------------------
 *    <copyright file="generated-reports-grid.data.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { GeneratedReport, GeneratedReportStatus, ReportModules } from '@interfaces/report-templates.interface';
import { TFunction } from 'i18next';
import React from 'react';
import { IExpandableGridColumnV2 } from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { GridColumnDateFilterV2 } from '@components/core/grid-filters-v2';
import StatusLabel from '@components/core/status-label';
import { reportStatusToLabelStatus } from '@components/shared/manage-reports-templates/generated-reports-grid/generated-reports-grid.helper';
import { useGridCells } from '@hooks/index';
import Constants from '@utils/constants';

const { DateCell, MaxLengthCell, CustomizableCell } = useGridCells();

type GeneratedReportColumns = keyof GeneratedReport | '_itemId' | '_status';

export const GENERATED_REPORTS_GRID_COLUMNS = (
    t: TFunction,
    module: ReportModules,
    isGenerationHistory?: boolean,
    includeNameAndVersion?: boolean,
    includeComparisonDataset?: boolean,
): IExpandableGridColumnV2[] => {
    const isCM = module === Constants.Modules.CM;
    const showNameAndVer = isGenerationHistory ? true : !!includeNameAndVersion;

    const templateCol: IExpandableGridColumnV2<GeneratedReportColumns> = {
        field: 'templateName',
        label: t('reports:templateName'),
        minWidth: 180,
        lockedByDefault: true,
        id: 'report-template-name',
    };

    const nameAndVersionCols = [
        {
            field: 'fullName',
            label: t('reports:reportName'),
            minWidth: Constants.ColumnWidth.default,
            lockedByDefault: !isGenerationHistory,
            id: 'report-name',
            cells: { data: MaxLengthCell(50) },
            showByDefault: showNameAndVer,
        },
        {
            field: 'reportVersion',
            label: t('reports:reportVersion'),
            minWidth: 120,
            id: 'report-version',
            cells: { data: MaxLengthCell(50) },
            showByDefault: showNameAndVer,
        },
    ];
    const datasetsCols = [
        {
            field: 'dataset',
            label: t('cm:dataset'),
            minWidth: 120,
            id: 'report-dataset',
            cells: { data: DateCell },
            filter: 'date',
            filterUi: GridColumnDateFilterV2,
        },
        {
            field: 'comparisonDataset',
            label: t('cm:comparisonDataset'),
            minWidth: 145,
            id: 'report-comparison-dataset',
            cells: { data: DateCell },
            filter: 'date',
            filterUi: GridColumnDateFilterV2,
            showByDefault: isGenerationHistory ? true : !!includeComparisonDataset,
        },
    ];

    const generatedDateAndByCols = [
        {
            field: 'createdDate',
            label: t('reports:generatedDate'),
            id: 'report-generated-date',
            minWidth: 140,
            cells: { data: (params: any) => DateCell({ ...params, dateFormat: 'DD-MMM-YYYY HH:mm:ss UTC' }) },
            filter: 'date',
            filterUi: GridColumnDateFilterV2,
        },
        {
            field: 'createdByName',
            label: t('reports:generatedBy'),
            id: 'report-generated-by',
            minWidth: Constants.ColumnWidth.default,
        },
    ];

    const statusAndCommentsCols = [
        {
            field: '_status',
            label: t('cm:status'),
            minWidth: 110,
            id: 'report-status',
            cells: {
                data: CustomizableCell((dataItem: any) => (
                    <StatusLabel
                        text={t(`reports:${GeneratedReportStatus[dataItem.status]}`)}
                        status={reportStatusToLabelStatus(dataItem.status)}
                    />
                )),
            },
        },
        {
            field: 'comments',
            label: t('cm:comments'),
            minWidth: Constants.ColumnWidth.default,
            id: 'risk-review-comments',
            cells: { data: MaxLengthCell(50) },
        },
    ];

    const recordIdCol: IExpandableGridColumnV2<GeneratedReportColumns> = {
        field: '_itemId',
        label: t('id'),
        minWidth: 85,
        id: 'risk-review-id',
        showByDefault: false,
    };

    if (isGenerationHistory) {
        return [
            templateCol,
            ...nameAndVersionCols,
            ...(isCM ? datasetsCols : []),
            ...statusAndCommentsCols,
            ...generatedDateAndByCols,
            recordIdCol,
        ];
    }

    return [
        ...nameAndVersionCols,
        ...(isCM ? datasetsCols : []),
        ...generatedDateAndByCols,
        ...statusAndCommentsCols,
        recordIdCol,
    ];
};
